import useUserEditVersion from '@/features/config/hooks/useUserEditVersion'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type FC,
  type ReactNode,
} from 'react'
import { Box, Stack, Popover, IconButton, Tooltip } from '@mui/material'
import { JoyStickIcon } from '@/components/icons'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { usePermission } from '@/hooks/permissions'
import { useTranslation } from '@/hooks/useTranslation'
import useAddController from '../../hooks/controller/useAddController'
import { useSearchDecoderType } from '../../hooks/controller'
import useSearchAllIOConfig from '../../hooks/controller/useSearchAllIOConfig'
import useSearchAllDecoders, {
  fetchAllDecoders,
} from '../../hooks/controller/useSearchAllDecoders'
import useMk2ImportSettling from '../../hooks/controller/useMk2ImportSettling'
import useDecoderProtocolMap from '../../hooks/controller/useDecoderProtocolMap'
import useIsControlHub from '../../hooks/controller/useIsControlHub'
import {
  getIOConfigOptions,
  resolveDecoderProtocol,
  resolveIOConfigId,
} from '@/utils/ioConfigOptions'
import { canAutoswitch } from '@/utils/decoderCapabilities'
import { runWhenIdle } from './runWhenIdle'
import { resolveDecoderPurposeId } from './decoderPurposeId'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/constant/common'
import DataGrid, {
  gridCellClasses,
  gridRowClasses,
} from '@/components/grid/DataGrid'
import type {
  GridApi,
  GridCellCtx,
  GridColumn,
  GridServerDatasource,
} from '@/components/grid/types'
import DecoderControlPanel from './DecoderControlPanel'
import DeleteDecoderDialog from './DeleteDecoderDialog'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import { controllerTokens } from './uiTokens'
import { IoTypeMask, LuProtocol } from '@/types/controller'
import classes from './controllers-table-view.module.scss'
import {
  PURPOSE_GROUP_LABEL_KEY,
  purposeGroupOf,
  sortPurposesByGroup,
} from './purposeGroups'
import PurposeChangeDialog from './PurposeChangeDialog'
import DecoderConflictInfo, {
  type DecoderConflict,
} from './DecoderConflictInfo'
import {
  addPurposePrompt,
  combinedImpact,
  hasImpact,
  purposeChangeImpact,
  removeDependents,
  withoutRemoved,
  NO_IMPACT,
  type PurposeChangeImpact,
  type PurposePromptEntry,
} from './decoderPurposeImpact'
import useSearchStation from '@/features/config/hooks/station/useSearchStation'
import useFlowValvesForPurposeCheck from '@/features/config/hooks/controller/useFlowValvesForPurposeCheck'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import {
  createStationRetype,
  stationTypeForPurpose,
} from '@/features/config/pages/StationTableView/station-retype'
import { missingStationFields } from '@/features/config/pages/StationTableView/station-completeness'
import flowValveApi from '@/api/config/flowValveApi'
import { getApiErrorMessage, isRetryableApiError } from '@/helpers/notify'
import { commitOpenEditor } from '@/components/grid/commitOpenEditor'
import type { GuidedSaveResult } from '@/features/config/contexts/guided-edit-context'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import { useGuidedIncomplete } from '@/features/config/contexts/guided-edit-context'
import {
  hasPurpose,
  resolvePurposeRow,
} from '@/features/design-system/components/pages/SetUpGuided/guidedStepGaps'
import {
  decoderConfigDisplay,
  type DecoderConfigState,
} from './decoderConfigDisplay'
import {
  loadGuidedDecoderProgress,
  saveGuidedDecoderProgress,
} from './guidedDecoderProgress'
import {
  getByDecoder,
  hasByDecoder,
  isLegacyAddressKey,
  isSamePhysicalDecoder,
  rowKeyOf,
  stationDecoderKeyOf,
} from './decoderRowKey'

// Rows are fetched a block at a time by the server-side row model.
const BLOCK_SIZE = 20
const ROW_HEIGHT = 48
// SCRUM-1032: the guided setup is a data-entry screen inside a wizard shell —
// header, footer and step rail already take their cut of the viewport, so the
// embedded grid runs a tighter row than the full /config page.
const EMBEDDED_ROW_HEIGHT = 40

export const DECODER_TYPE_BY_TAB: Record<string, number> = {
  '0': IoTypeMask.Output,
  '1': IoTypeMask.Input,
  '2': IoTypeMask.Sdi12,
}

// Scoped per tab: Output and Input show different column sets, so a shared key
// meant each tab clobbered the other's column order and visibility.
const columnStateKey = (tab: string) => `controllers-table-${tab}-column-state`

// Decoder writes must be serialised: the API returns 500 under concurrent
// writes, which silently drops most rows of a large bulk edit.
const SAVE_RETRY_LIMIT = 3
/** Beyond a few names a toast stops being readable; the rest stay amber. */
const MAX_FAILED_NAMES_SHOWN = 3
const SAVE_RETRY_DELAY_MS = 300
const SAVE_REQUEST_GAP_MS = 120

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

type DecoderRow = Record<string, any>

/** The decoder purpose a station row carries, as a name. */
const stationPurposeOf = (station: any): string =>
  String(
    station?.decoderType?.description ??
      station?.decoderType?.name ??
      station?.decoderType ??
      '',
  ).trim()

/**
 * SCRUM-1145 — how a station disagrees with its decoder, if it does.
 *
 * `stranded`: the purpose is no longer a station purpose at all.
 * `mismatch`: still one, but the station is the other Type. Not judged while
 * the purpose is a staged edit: the station has not had its chance to follow.
 */
/** The fields an Irrigation station needs on top of a Non Irrigation one. */
const IRRIGATION_ONLY_FIELDS = [
  'plant',
  'soil',
  'emitter',
  'pipe',
  'expectedFlow',
  'precipitationRate',
  'priority',
]

/**
 * What `station` is missing before it can be an Irrigation station. A Non
 * Irrigation station keeps its plant, soil and emitter (SCRUM-1157) but loses
 * its pipe and flow figures, so this is usually the pipe and the figures, and
 * the agronomy too when it never had any.
 */
const irrigationFieldsMissing = (station: any): string[] =>
  missingStationFields({ ...station, stationType: 2 }).filter((field) =>
    IRRIGATION_ONLY_FIELDS.includes(field),
  )

const conflictKindOf = (
  station: any,
  purpose: string,
  purposeStaged: boolean,
): DecoderConflict['kind'] | null => {
  // No purpose at all says nothing either way.
  if (!purpose || purpose === 'null') return null
  const expectedType = stationTypeForPurpose(purpose)
  if (!expectedType) return 'stranded'
  if (!purposeStaged && Number(station?.stationType) !== expectedType)
    return 'mismatch'
  return null
}

// Column configuration.
// Sorting is server-side, so a column is only marked sortable when the API
// actually honours that SortBy value. Verified honoured: name, address,
// configState (groups configured/unconfigured), configuration (by name) and
// verifySum (SCRUM-1029).
// index / value / configurationId / decoderTypeId are ignored by the endpoint
// (it falls back to Id order for them), so they are left unsortable rather
// than looking sortable and doing nothing. With no SortBy at all the endpoint
// sorts by name, which is why the list asks for address order explicitly when
// the user has not chosen a sort (SCRUM-1032 QC round 7, item 10).
// The one server-side filter the endpoint supports is Purpose (a repeated
// Purpose parameter, wired on the decoderType column). Everything else is off:
// rows arrive a block at a time, so a client-side filter would only ever see
// what has been fetched so far.
const COLUMN_CONFIG = [
  {
    // Pinned first-column button that opens the per-decoder control panel
    // (IO value, device commands, line-unit + IO readings). Output tab only —
    // input / SDI-12 decoders have no commands to drive from here.
    field: 'controlPanel',
    headerName: '',
    valueType: 'controlPanel',
    pinned: 'left' as const,
    sortable: false,
    resizable: false,
    minWidth: 74,
    maxWidth: 78,
    width: 74,
    conditionalHide: 'tabOutput',
  },
  {
    field: 'name',
    headerName: 'controllersColumnDecoderName',
    minWidth: 150,
    sortable: true,
    editable: true,
  },
  {
    field: 'twiCustomName',
    headerName: 'controllersColumnController',
    minWidth: 185,
    sortable: false,
    conditionalHide: 'controlHub',
    valueType: 'controller',
  },
  {
    // Address and index merged into one "(address)-(index)" value: the index
    // disambiguates decoders that share an address (e.g. SDI-12). Sort still
    // runs on the address field server-side.
    field: 'address',
    headerName: 'controllersColumnAddress',
    minWidth: 120,
    sortable: true,
    valueType: 'addressIndex',
  },
  {
    field: 'decoderType',
    headerName: 'controllersColumnPurpose',
    minWidth: 150,
    sortable: true,
    conditionalHide: 'tabOutput',
    editable: true,
    valueType: 'richSelect',
  },
  {
    // Configuration merged with config state: the value is the config name,
    // prefixed with a coloured dot (green configured / amber unconfigured) whose
    // full word shows in a tooltip. Sort still runs on config state server-side.
    field: 'configuration',
    headerName: 'controllersColumnConfiguration',
    minWidth: 160,
    sortable: true,
    editable: true,
    valueType: 'richSelectConfig',
  },
  {
    field: 'value',
    headerName: 'formsLabelValue',
    minWidth: 120,
    sortable: false,
    valueType: 'valueModified',
  },
  {
    // Verify Sum is only meaningful for output decoders.
    field: 'verifySum',
    headerName: 'controllersColumnVerifySum',
    minWidth: 100,
    // Server-side sort added by SCRUM-1029.
    sortable: true,
    conditionalHide: 'tabOutput',
  },
  {
    field: 'deleteAction',
    headerName: '',
    valueType: 'deleteAction',
    pinned: 'right' as const,
    sortable: false,
    resizable: false,
    minWidth: 72,
    maxWidth: 72,
    width: 72,
    conditionalHide: 'bulkEdit',
  },
]

// SCRUM-1032: the guided list is read controller-first — the user works down a
// physical controller's decoders, so which controller a row belongs to and its
// address on that controller are what they scan for. `/config` keeps the
// name-first order it has always had.
const GUIDED_COLUMN_ORDER = [
  'controlPanel',
  'twiCustomName',
  'address',
  'name',
  'decoderType',
  'configuration',
]

// "Address" alone reads as a site address on a setup screen full of them, so
// the guided list spells it out. Values are translation keys.
const GUIDED_HEADER_OVERRIDE_KEYS: Record<string, string> = {
  address: 'decoderAddress',
}

// SCRUM-1032 QC: columns the discovery review does not show.
//
// Discovery answers one question — what did the controller come back with —
// and the decoder's name and purpose are not part of that answer: both are
// entered later, in the guided setup's decoder steps, and showing them here
// invites editing on a screen that is deliberately read-only. The guided steps
// and `/config` keep both columns.
const REVIEW_HIDDEN_COLUMNS = ['name', 'decoderType']

// --- Grid context ----------------------------------------------------------
// The cell renderers are module-level components on purpose: the control panel
// holds an open popover, and rebuilding its component type whenever a command
// starts or the theme flips would unmount that popover mid-command. They read
// the live values from context instead, which re-renders them in place.

interface ControllersGridContextValue {
  tab: string
  interfaceId: number
  isDark: boolean
  /** The row's line-unit protocol, or 0 when it cannot be resolved. */
  resolveRowProtocol: (row: DecoderRow) => number
  onOn: (id: string, address: string, rowKey: string) => void
  onOff: (id: string, address: string, rowKey: string) => void
  onPing: (id: string, address: string, rowKey: string) => void
  onAutoswitch: (id: string, address: string, rowKey: string) => void
  onDelete: (row: DecoderRow) => void
  onDecoderDeleted: (row: DecoderRow) => void
  loadingRows: Record<string, boolean>
  globalLoading: boolean
  /** SCRUM-1145 — flagged decoders by row key; see `decoderConflicts`. */
  decoderConflicts: Map<string, DecoderConflict>
}

const ControllersGridContext =
  createContext<ControllersGridContextValue | null>(null)

const useControllersGrid = () => {
  const value = useContext(ControllersGridContext)
  if (!value) throw new Error('ControllersGridContext is missing')
  return value
}

// Shows value with a small dropdown arrow, so an editable cell reads as one.
const DropdownCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      height: '100%',
      cursor: 'pointer',
    }}
  >
    <span>{(value as ReactNode) ?? ''}</span>
    <svg
      width="8"
      height="5"
      viewBox="0 0 8 5"
      style={{ flexShrink: 0, marginLeft: 4 }}
    >
      <path d="M0 0l4 5 4-5z" fill="currentColor" opacity="0.4" />
    </svg>
  </Box>
)

// The editable Purpose cell: a flagged decoder keeps its red mark and info
// button while the user edits, since editing is how it gets fixed.
const PurposeDropdownCellRenderer: FC<GridCellCtx<DecoderRow>> = (props) => {
  const { decoderConflicts, isDark } = useControllersGrid()
  const conflict = props.row ? getByDecoder(decoderConflicts, props.row) : undefined
  if (!conflict) return <DropdownCellRenderer {...props} />
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
        height: '100%',
        color: isDark ? '#FDA29B' : '#B42318',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
        <DropdownCellRenderer {...props} />
      </Box>
      <DecoderConflictInfo conflict={conflict} isDark={isDark} />
    </Box>
  )
}

/** The state word shown on hover, translated when it is one we know. */
const CONFIG_STATE_LABEL_KEY: Partial<Record<DecoderConfigState, string>> = {
  configured: 'controllersConfigStateConfigured',
  unconfigured: 'controllersConfigStateUnconfigured',
  autoswitched: 'controllersConfigStateAutoswitched',
}

const useConfigDisplay = (row: DecoderRow | undefined, value: unknown) => {
  const { t } = useTranslation()
  const display = decoderConfigDisplay(row)
  const name =
    display.label === 'autoswitch' ? t('Autoswitch') : String(value ?? '')
  const stateKey = CONFIG_STATE_LABEL_KEY[display.state]
  const stateLabel = stateKey
    ? t(stateKey)
    : display.rawState
      ? display.rawState.charAt(0).toUpperCase() + display.rawState.slice(1)
      : ''
  return { display, name, stateLabel }
}

// The editable Configuration cell (QC round 2, item 1): the same wording as the
// read-only one, so an autoswitched decoder reads "Autoswitch" here too.
const ConfigDropdownCellRenderer: FC<GridCellCtx<DecoderRow>> = ({
  row,
  value,
}) => {
  const { name, stateLabel } = useConfigDisplay(row, value)
  return (
    <Tooltip title={stateLabel} arrow placement="top" disableInteractive>
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <DropdownCellRenderer {...({ row, value: name } as any)} />
      </Box>
    </Tooltip>
  )
}

// --- Pill / chip helpers ----------------------------------------------------
// A richer, more scannable grid: state columns read as coloured pills instead
// of flat grey text. Colours are theme-aware (light / dark) via grid context.
type PillTone = 'green' | 'grey' | 'amber' | 'brand' | 'neutral' | 'red'

// Aligned to the UI-revamp §2 palette (see uiTokens.ts): success = brand green
// (mint on dark), warn = amber, off/neutral = teal-biased greys.
const PILL_COLORS: Record<
  PillTone,
  {
    light: { bg: string; fg: string; border: string }
    dark: { bg: string; fg: string; border: string }
  }
> = {
  green: {
    light: { bg: '#E7F8F0', fg: '#027A48', border: '#C5E9D7' },
    dark: {
      bg: 'rgba(102, 240, 160, 0.14)',
      fg: '#66F0A0',
      border: 'rgba(102, 240, 160, 0.30)',
    },
  },
  grey: {
    light: { bg: '#F2F4F5', fg: '#667085', border: '#E4E7EC' },
    dark: {
      bg: 'rgba(255, 255, 255, 0.07)',
      fg: '#8A9AA3',
      border: 'rgba(255, 255, 255, 0.14)',
    },
  },
  amber: {
    light: { bg: '#FDF0E1', fg: '#B5540A', border: '#F3D9BC' },
    dark: {
      bg: 'rgba(238, 205, 29, 0.16)',
      fg: '#EECD1D',
      border: 'rgba(238, 205, 29, 0.34)',
    },
  },
  brand: {
    light: { bg: '#E7F1EF', fg: '#004744', border: '#D3E4E0' },
    dark: {
      bg: 'rgba(102, 240, 160, 0.12)',
      fg: '#66F0A0',
      border: 'rgba(102, 240, 160, 0.24)',
    },
  },
  neutral: {
    light: { bg: '#F2F5F5', fg: '#475467', border: '#E4EAEA' },
    dark: {
      bg: 'rgba(255, 255, 255, 0.05)',
      fg: '#A9BAC2',
      border: 'rgba(255, 255, 255, 0.10)',
    },
  },
  // SCRUM-1145 — a decoder whose station no longer matches it.
  red: {
    light: { bg: '#FEF3F2', fg: '#B42318', border: '#FECDCA' },
    dark: {
      bg: 'rgba(253, 162, 155, 0.14)',
      fg: '#FDA29B',
      border: 'rgba(253, 162, 155, 0.34)',
    },
  },
}

const StatusPill = ({
  label,
  tone,
  isDark,
  dot,
}: {
  label: ReactNode
  tone: PillTone
  isDark?: boolean
  dot?: boolean
}) => {
  const c = PILL_COLORS[tone][isDark ? 'dark' : 'light']
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: '10px',
        height: '23px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        backgroundColor: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: c.fg,
          }}
        />
      )}
      <Box
        component="span"
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Box>
    </Box>
  )
}

const ValueModifiedCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ row }) => {
  const { tab, isDark } = useControllersGrid()
  const { t } = useTranslation()

  if (tab === '0') {
    const on = Number(row?.value) !== 0
    return (
      <StatusPill
        label={on ? t('mapViewStationOn') : t('mapViewStationOff')}
        tone={on ? 'green' : 'grey'}
        isDark={isDark}
        dot
      />
    )
  }
  return <span>{Number(row?.value)}</span>
}

// Controller (sub-controller name on a hub) as a quiet neutral chip.
const ControllerChipCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ value }) => {
  const { isDark } = useControllersGrid()
  if (value == null || value === '') return null
  return (
    <StatusPill label={value as ReactNode} tone="neutral" isDark={isDark} />
  )
}

// Purpose (Irrigation, Fertigation…) as a brand chip in view mode. A flagged
// decoder's chip is red, with an info button saying why (SCRUM-1145).
const PurposeChipCellRenderer: FC<GridCellCtx<DecoderRow>> = ({
  row,
  value,
}) => {
  const { decoderConflicts, isDark } = useControllersGrid()
  if (value == null || value === '') return null
  const conflict = row ? getByDecoder(decoderConflicts, row) : undefined
  if (!conflict)
    return <StatusPill label={value as ReactNode} tone="brand" isDark={isDark} />
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%' }}
    >
      <StatusPill label={value as ReactNode} tone="red" isDark={isDark} />
      <DecoderConflictInfo conflict={conflict} isDark={isDark} />
    </Box>
  )
}

// Merged Configuration + Config State: the config name, led by a coloured dot
// (green configured / amber unconfigured) whose full word shows on hover. The
// placeholder "Unknown" config name is dimmed.
const ConfigCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ row, value }) => {
  const { isDark } = useControllersGrid()
  // QC round 2, item 1: the id decides, the (possibly stale) state refines.
  const { display, name, stateLabel } = useConfigDisplay(row, value)
  if (!name) return null

  const tone: PillTone =
    display.state === 'configured'
      ? 'green'
      : display.state === 'unconfigured'
        ? 'amber'
        : 'grey'
  const dotColor = PILL_COLORS[tone][isDark ? 'dark' : 'light'].fg
  const muted = display.label === 'name' && name.toLowerCase() === 'unknown'

  return (
    <Tooltip title={stateLabel} arrow placement="top" disableInteractive>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <Box
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: dotColor,
          }}
        />
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: muted ? (isDark ? '#7c8b96' : '#9aa4ae') : 'inherit',
            fontStyle: muted ? 'italic' : 'normal',
          }}
        >
          {name}
        </Box>
      </Box>
    </Tooltip>
  )
}

// A single pinned button per output-decoder row that opens a control-panel
// popover: the On/Off/Ping/Autoswitch device commands plus IO Readings, so the
// row itself stays clean instead of carrying five inline buttons.
const ControlPanelCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ row }) => {
  const {
    tab,
    interfaceId,
    resolveRowProtocol,
    onOn,
    onOff,
    onPing,
    onAutoswitch,
    onDecoderDeleted,
    loadingRows,
    globalLoading,
    isDark,
  } = useControllersGrid()

  // Line-unit / IO readings are only meaningful for mk3-protocol output
  // decoders, so gate those panel sections on the row's resolved protocol.
  // Autoswitch goes the other way: mk2 hardware cannot do it at all.
  const protocol = resolveRowProtocol ? resolveRowProtocol(row) : 0
  const isMk3 = protocol === LuProtocol.Mk3
  const canRunAutoswitch = canAutoswitch(protocol)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const close = () => setAnchorEl(null)

  const isLoading = loadingRows[rowKeyOf(row)] || false

  const c = controllerTokens(isDark)
  // State stripe shows only for output decoders and encodes On/Off: accent when
  // energised, muted when off. Other decoder types get a transparent spacer so
  // the control button stays aligned.
  const isOutput = tab === '0'
  const stripe = !isOutput
    ? 'transparent'
    : Number(row?.value) !== 0
      ? c.accent
      : c.faint

  return (
    <Box
      data-no-cell-edit
      sx={{ display: 'flex', alignItems: 'center', gap: '9px', height: '100%' }}
    >
      <Box
        sx={{
          width: '3px',
          height: '26px',
          flexShrink: 0,
          borderRadius: '3px',
          backgroundColor: stripe,
        }}
      />
      <IconButton
        size="small"
        disabled={globalLoading}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: 30,
          height: 30,
          borderRadius: '8px',
          color: c.chipFg,
          backgroundColor: c.chipBg,
          border: `1px solid ${isDark ? 'rgba(102,240,160,0.30)' : 'rgba(0,71,68,0.18)'}`,
          transition: 'background-color .15s ease, transform .15s ease',
          '&:hover': {
            backgroundColor: isDark
              ? 'rgba(102,240,160,0.24)'
              : 'rgba(0,71,68,0.14)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <JoyStickIcon width={16} height={16} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              borderRadius: '16px',
              maxHeight: '82vh',
              overflowY: 'auto',
              backgroundColor: 'transparent',
              boxShadow: isDark
                ? '0 18px 44px -12px rgba(0,0,0,0.6)'
                : '0 18px 44px -16px rgba(16,24,40,0.28)',
              border: `1px solid ${c.line}`,
            },
          },
        }}
      >
        <DecoderControlPanel
          data={row}
          tab={tab}
          isMk3={isMk3}
          protocol={protocol}
          canAutoswitch={canRunAutoswitch}
          fallbackInterfaceId={interfaceId}
          isDark={isDark}
          isCommandLoading={isLoading}
          onOn={onOn}
          onOff={onOff}
          onPing={onPing}
          onAutoswitch={onAutoswitch}
          onClose={close}
          onDeleted={() => onDecoderDeleted(row)}
        />
      </Popover>
    </Box>
  )
}

const DeleteActionCellRenderer: FC<GridCellCtx<DecoderRow>> = ({ row }) => {
  const { isDark, onDelete } = useControllersGrid()
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  if (!row?.id) return null
  return (
    <Stack
      data-no-cell-edit
      sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}
    >
      <DeleteIconButton
        disabled={isViewOnly}
        isDark={isDark}
        title={t('commonActionsDelete')}
        onClick={() => onDelete(row)}
      />
    </Stack>
  )
}

export interface ControllersTableViewRef {
  /**
   * `force` is the user asking, through Save or through the wizard moving on.
   * It overrides a Stop, which only ever silences the background autosave.
   */
  handleSave: (options?: { force?: boolean }) => Promise<GuidedSaveResult>
  handleCancel: () => void
  /** Rows still staged — after a save, the ones that failed to write. */
  hasPendingChanges: () => boolean
  /** Re-reads the decoders, e.g. after something else wrote them. */
  refresh: () => void
}

export type AutosaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

interface ControllersTableViewProps {
  tab: string
  interfaceId: number
  keyword?: string
  bulkEditMode?: boolean
  onToggleBulkEdit?: () => void
  /** Guided setup renders a compact, independently-persisted decoder grid. */
  embedded?: boolean
  /**
   * Discovery review: a read-only look at what the controller returned, which
   * drops the columns the setup steps own (see REVIEW_HIDDEN_COLUMNS). Distinct
   * from `embedded`, which the guided decoder steps also set — they are the
   * screen where name and purpose get filled in.
   */
  review?: boolean
  onAutosaveStateChange?: (
    state: AutosaveState,
    detail?: {
      lastSavedAt?: number
      /** Rows settled out of the batch, so a long flush reads as moving. */
      progress?: { done: number; total: number }
      /** Abandons the rest of the batch; the unwritten rows stay staged. */
      onStop?: () => void
    },
  ) => void
  /** Which rows the guided toolbar is asking for: all, only edited, only not. */
  editedFilter?: 'all' | 'edited' | 'notEdited' | 'missingPurpose'
  /** Reports how much of the list this user has edited, for the toolbar. */
  onEditedProgress?: (progress: {
    edited: number
    total: number | null
    /** Output decoders with no purpose yet, staged edits included. */
    missingPurpose: number
  }) => void
  /**
   * Holds the background autosave. Set while the Apply Configuration run is
   * open: both write whole decoder rows, and whichever lands second puts back
   * what the other changed (SCRUM-1032 QC round 2, item 3).
   */
  autosavePaused?: boolean
}

const ControllersTableView = forwardRef<
  ControllersTableViewRef,
  ControllersTableViewProps
>((props, ref) => {
  const {
    tab,
    interfaceId,
    keyword = '',
    bulkEditMode = false,
    onToggleBulkEdit,
    embedded = false,
    review = false,
    onAutosaveStateChange,
    editedFilter = 'all',
    onEditedProgress,
    autosavePaused = false,
  } = props
  const { t } = useTranslation()
  const { isViewOnly } = usePermission()
  const queryClient = useQueryClient()

  const gridApiRef = useRef<GridApi<DecoderRow> | null>(null)
  const editedRowsRef = useRef<Record<string, DecoderRow>>({})
  /**
   * The pass currently writing, if any. One at a time: the endpoint 500s on
   * overlap. Held as the promise rather than a flag so a forced save — Save,
   * or the wizard moving on — can wait for it instead of returning to a caller
   * that then treats the list as flushed.
   */
  const inFlightRef = useRef<Promise<GuidedSaveResult> | null>(null)
  /**
   * The fields written per row, kept only for as long as their staged values
   * are still on screen.
   *
   * The grid paints its own amber from its pending-edit buffer, and that buffer
   * cannot be emptied per row: the base row underneath still holds the value
   * the save replaced, so dropping the staged one would snap the cell back to
   * the old text until the end-of-pass refetch. So the cell keeps its staged
   * value and is given the saved class instead, which is declared after the
   * amber and takes it. Emptied when the pass ends and the rows are re-read.
   */
  const savedFieldsRef = useRef<Record<string, Set<string>>>({})
  /**
   * The purpose last written for a row, by row key.
   *
   * A saved row loses its staged edit the moment its request comes back, but
   * the decoder list is only re-read at the end of the pass — so between the
   * two the row reads as it did on the server, without a purpose. Counting
   * those made the "without purpose" total climb as the save progressed
   * instead of falling (SCRUM-1032, found by QC 22 Sep). Holds the empty
   * string for a purpose that was cleared, so this never masks one.
   */
  const savedPurposeRef = useRef<Record<string, string>>({})
  /** Stop was pressed: abandon the rest of the batch mid-loop. */
  const stopRequestedRef = useRef(false)
  /** ...and do not start another by itself until the user edits again. */
  const stoppedRef = useRef(false)
  const autosavePausedRef = useRef(autosavePaused)
  autosavePausedRef.current = autosavePaused

  // Pending edits live in a ref so a block arriving mid-edit can be re-merged
  // synchronously; this counter is what tells React the ref has moved.
  const [editVersion, setEditVersion] = useState(0)
  /**
   * Moves on user edits only, for the autosave. `editVersion` also moves as
   * the save repaints each row, and arming the autosave on it resent a
   * refused PUT every ~2.5 s for as long as the step was open (D2 retest,
   * 29 Oct) — the same loop Hydraulic had in round 7.
   */
  const [userEditVersion, noteUserEdit] = useUserEditVersion()

  const [deleteTarget, setDeleteTarget] = useState<DecoderRow | null>(null)

  const handleDecoderDeleted = useCallback((deleted: DecoderRow) => {
    let dropped = false
    Object.entries(editedRowsRef.current).forEach(([key, row]) => {
      if (!isSamePhysicalDecoder(row, deleted)) return
      delete editedRowsRef.current[key]
      dropped = true
    })
    if (dropped) setEditVersion((version) => version + 1)
  }, [])
  // `-v2`: the stored state carries column *order*, so the guided list would
  // keep the old name-first order for anyone who has opened it before.
  const storageTab = embedded ? `${tab}-guided-v2` : tab
  // Edited rows are keyed off the decoder type alone: they outlive any
  // column-state version bump.
  const progressTab = `${tab}-guided`

  // Rows this user has edited. Guided setup only; the /config screen is not a
  // walk-the-whole-list task.
  const [editedRowKeys, setEditedRowKeys] = useState<Set<string>>(new Set())
  /**
   * Rows written to the server this sitting, by `address-index`.
   *
   * The tint that says a row has landed. Filled one row at a time as each
   * request comes back rather than at the end of the batch: the decoders are
   * written one per request, so a list of two hundred sat entirely amber for
   * the length of the flush and then turned over at once — which reads as
   * stuck, not as slow. Not persisted; it describes this sitting only.
   */
  const [savedRowKeys, setSavedRowKeys] = useState<Set<string>>(new Set())
  /** True while a save pass is writing, which makes the grid read-only. */
  const [isSaving, setIsSaving] = useState(false)
  const [serverTotal, setServerTotal] = useState<number | null>(null)

  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({})
  const [globalLoading, setGlobalLoading] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  // Whether this interface is a control hub, which decides if the Controller
  // column is shown. It is a property of the interface (it has sub-controllers)
  // rather than of the current tab's rows, so it stays consistent across the
  // Output / Input / SDI-12 tabs.
  const { data: hasControlHub } = useIsControlHub(interfaceId)

  const isDark = useSelector(selectTheme) === mode_type.dark

  const {
    mutate: addController,
    isSuccess,
    isError: isErrorAdd,
  } = useAddController()

  const { data: decoderTypeData } = useSearchDecoderType({
    PageSize: 100,
    interfaceId,
  })
  const decoderTypeOptions = useMemo(
    () => decoderTypeData?.list?.map((dt) => dt.name) ?? [],
    [decoderTypeData],
  )

  // The editor's list, in families. A dozen terms from one domain read as a
  // wall; grouped, the eye aims rather than reads. Sorted here because MUI
  // starts a new heading whenever the group changes.
  const groupedPurposeOptions = useMemo(
    () => sortPurposesByGroup(decoderTypeOptions),
    [decoderTypeOptions],
  )

  // Each tab is one SRC decoder type. The list endpoint takes a single type
  // per query, so they cannot be combined into one tab.
  const ioConfigType = DECODER_TYPE_BY_TAB[tab] ?? IoTypeMask.Output

  // SCRUM-1032: the guided setup holds the whole decoder list in memory rather
  // than paging it. The user is walking every row once — a list that arrives 20
  // at a time as they scroll cannot say how much is left, cannot be searched or
  // sorted without a round trip, and cannot tell the Station step which
  // decoders exist. /config keeps the block cache: there the list is browsed,
  // not walked.
  const {
    data: allDecoders,
    isFetching: isFetchingAllDecoders,
    refetch: refetchAllDecoders,
  } = useSearchAllDecoders(interfaceId, ioConfigType, { enabled: embedded })
  // An MK2 import still waiting for its decoders (SCRUM-1032 QC round 7, item
  // 11): the overlay, rather than an empty list that reads as "no decoders".
  const isImportSettling = useMk2ImportSettling(Number(interfaceId))

  /**
   * Re-reads the list. Awaitable in the guided embed, where the caller needs to
   * know the new rows have arrived before it drops what is on screen.
   */
  /**
   * SCRUM-1145 — the whole list, flagged first, when the grid is serving it
   * itself (see `datasource`). Dropped on every refresh so a save shows.
   */
  const liftedListRef = useRef<Promise<DecoderRow[]> | null>(null)

  const refreshRows = useCallback(async () => {
    if (embedded) {
      await refetchAllDecoders()
      return
    }
    liftedListRef.current = null
    gridApiRef.current?.refreshServerSide()
  }, [embedded, refetchAllDecoders])

  // The rows come from a datasource, not from React Query, but plenty of other
  // places still signal "decoders changed" by invalidating the controller
  // query: the sidebar editor, the On/Off/Ping/Autoswitch commands, Discover
  // Decoder, the Mk2 import and the configuration modal. Bridge those
  // invalidations to a cache refresh so the grid keeps updating for them.
  useEffect(() => {
    const cache = queryClient.getQueryCache()
    let pending: ReturnType<typeof setTimeout> | undefined

    const unsubscribe = cache.subscribe((event: any) => {
      if (event?.type !== 'updated') return
      if (event.action?.type !== 'invalidate') return
      if (event.query?.queryKey?.[0] !== queryKeys.controller) return

      // Several mutations invalidate in quick succession; coalesce them.
      clearTimeout(pending)
      pending = setTimeout(refreshRows, 150)
    })

    return () => {
      clearTimeout(pending)
      unsubscribe()
    }
  }, [queryClient, refreshRows])

  // The grid asks for rows a block at a time and owns the scroll position and
  // the sort round trip. Sorting and searching therefore run on the server,
  // across all decoders, rather than over whatever happens to be loaded.
  /**
   * Every station with its decoder's purpose, for the stranded-decoder warning
   * on /config (SCRUM-1145).
   *
   * Its own key rather than `useSearchStation`'s: the operations hook of the
   * same name caches under `[stations, interfaceId, { PageSize: 0 }]` too, with
   * rows that carry no description, and whichever mounted first won.
   */
  const {
    data: decoderStations,
    isFetched: decoderStationsFetched,
    isError: decoderStationsFailed,
  } = useQuery(
    [queryKeys.stations, Number(interfaceId), 'decoder-purposes'],
    async () => {
      const { data } = await stationDataConfigApi.getAll({
        interfaceId: Number(interfaceId),
        PageSize: 0,
        PageIndex: 1,
      } as any)
      return (data?.data ?? []) as any[]
    },
    { enabled: !embedded && tab === '0' && Number(interfaceId) > 0 },
  )

  /**
   * SCRUM-1145 — the flagged decoders, from what is saved: the ones the grid
   * lifts to the top. Staged edits are left out on purpose; reordering under
   * the user while they pick a purpose would move the row they are editing.
   */
  const liftedConflictKeys = useMemo(() => {
    const keys = new Set<string>()
    if (embedded || tab !== '0') return keys
    ;(decoderStations ?? []).forEach((station: any) => {
      if (conflictKindOf(station, stationPurposeOf(station), false))
        keys.add(stationDecoderKeyOf(station))
    })
    return keys
  }, [embedded, tab, decoderStations])
  /**
   * Whether the order is known yet. The first blocks wait for the stations
   * rather than arriving in server order and being reshuffled a moment later.
   * A failed read lets the grid load as it always did.
   */
  const liftReady =
    embedded ||
    tab !== '0' ||
    !(Number(interfaceId) > 0) ||
    decoderStationsFetched ||
    decoderStationsFailed
  // A string, so the datasource is rebuilt when the set changes and not on
  // every refetch that returns the same stations.
  const liftedConflictSignature = useMemo(
    () => Array.from(liftedConflictKeys).sort().join('|'),
    [liftedConflictKeys],
  )

  const datasource = useMemo<GridServerDatasource<DecoderRow>>(() => {
    liftedListRef.current = null
    return {
      getRows: async ({
        startRow,
        endRow,
        sortModel,
        filterModel,
        success,
        fail,
      }) => {
        const pageSize = endRow - startRow
        const pageIndex = Math.floor(startRow / pageSize) + 1
        const sort = sortModel?.[0]
        // Purpose is the one column the API can filter on, via a repeated
        // Purpose parameter holding decoder type ids.
        const purposeIds: number[] =
          ((filterModel?.decoderType as any)?.values as number[]) ?? []

        /**
         * SCRUM-1145 — flagged decoders first, so the user does not scroll
         * five hundred rows to find the ones that need them. The API pages and
         * has no such order, so the list is read whole and paged here. Only
         * while nothing else orders it: a sort, a search or a filter the user
         * chose wins, and with nothing flagged the server pages as before.
         */
        // Answered once the stations are in: `liftReady` is a dependency, so
        // the datasource is rebuilt then, which purges and asks again. This
        // request belongs to the old generation and is dropped either way.
        if (!liftReady) return

        const lift =
          liftedConflictSignature !== '' &&
          !sort &&
          !keyword &&
          purposeIds.length === 0
        if (lift) {
          try {
            if (!liftedListRef.current) {
              liftedListRef.current = fetchAllDecoders(
                interfaceId,
                ioConfigType,
              ).then((all) => {
                const flagged = new Set(liftedConflictSignature.split('|'))
                return [
                  ...all.filter((row) => hasByDecoder(flagged, row)),
                  ...all.filter((row) => !hasByDecoder(flagged, row)),
                ]
              })
            }
            const ordered = await liftedListRef.current
            success(ordered.slice(startRow, endRow), ordered.length)
          } catch (error: any) {
            liftedListRef.current = null
            toast.error(getApiErrorMessage(error, t('somethingWentWrong')), {
              id: 'decoder-list-load-failed',
            })
            fail()
          }
          return
        }

        try {
          const res: any = await controllerDataConfigApi.getAll({
            interfaceId,
            type: ioConfigType,
            PageIndex: pageIndex,
            PageSize: pageSize,
            ...(keyword ? { Keyword: keyword } : undefined),
            ...(purposeIds.length ? { Purpose: purposeIds } : undefined),
            ...(sort
              ? { SortBy: sort.field, SortDirection: sort.sort }
              : { SortBy: 'address', SortDirection: 'asc' }),
          } as any)

          const payload = res?.data
          success(payload?.data ?? [], payload?.totalCount ?? 0)
        } catch (error: any) {
          // The server's own words when it gave any: a 400 whose body reads
          // `{ message: "bad request" }` used to surface as the generic
          // "Something Went Wrong", because only the capital-M spelling was
          // read (SCRUM-1032 QC, 21 Sep).
          // One id for the whole list: the grid asks for a block at a time, so
          // a controller that is refusing answers every block with the same
          // 400 and the toasts used to stack one per request.
          toast.error(getApiErrorMessage(error, t('somethingWentWrong')), {
            id: 'decoder-list-load-failed',
          })
          fail()
        }
      },
    }
    // `t` is left out (react-hooks rule); the rest rebuild the datasource.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfaceId, ioConfigType, keyword, liftedConflictSignature, liftReady])

  /**
   * QC round 2, item 2 — purpose is required on every output decoder in the
   * wizard. Counted over the whole list with staged edits applied, so the
   * count moves as the user picks, not once autosave has caught up.
   */
  const requiresPurpose = embedded && !review && tab === '0'
  const missingPurposeKeys = useMemo(() => {
    if (!requiresPurpose || !allDecoders) return null
    const edits = editedRowsRef.current
    const written = savedPurposeRef.current
    const keys = new Set<string>()
    allDecoders.forEach((row) => {
      const key = rowKeyOf(row as DecoderRow)
      // Staged edit first, then what this sitting wrote, then the server row.
      if (!hasPurpose(resolvePurposeRow(row, edits[key], written[key]))) {
        keys.add(key)
      }
    })
    return keys
    // `editVersion` is what moves when `editedRowsRef` is written in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresPurpose, allDecoders, editVersion])
  const missingPurposeCount = missingPurposeKeys?.size ?? 0

  useGuidedIncomplete(
    'decodersOutput',
    missingPurposeCount > 0
      ? t(
          missingPurposeCount === 1
            ? 'guidedSetupGapDecoderPurposeOne'
            : 'guidedSetupGapDecoderPurposeOther',
          { count: missingPurposeCount },
        )
      : null,
  )

  // Search over the loaded list rather than a round trip, and re-apply pending
  // edits the same way `transformBlock` does for the paged path.
  const guidedRows = useMemo(() => {
    if (!embedded) return undefined
    const edits = editedRowsRef.current
    const rows = (allDecoders ?? []).map(
      (row) => edits[rowKeyOf(row)] ?? row,
    ) as DecoderRow[]

    const needle = keyword.trim().toLowerCase()
    if (!needle) return rows

    return rows.filter((row) =>
      [
        row.name,
        `${row.address}-${row.index}`,
        row.address,
        row.twiCustomName,
        row.decoderType,
        row.configuration,
      ].some((field) =>
        String(field ?? '')
          .toLowerCase()
          .includes(needle),
      ),
    )
    // `editedRowsRef` is mutated in place; the counter marks it as moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, allDecoders, keyword, editVersion])

  // Re-apply pending bulk edits on the way to the screen: the grid may drop and
  // refetch a block at any time, which would otherwise discard unsaved changes.
  const transformBlock = useCallback(
    (rows: DecoderRow[]) => {
      const edits = editedRowsRef.current
      if (Object.keys(edits).length === 0) return rows
      return rows.map((row) => edits[rowKeyOf(row)] ?? row)
    },
    // The ref is mutated in place; the counter is what marks it as moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editVersion],
  )

  // Every configuration for the interface, fetched once, plus the protocol of
  // each decoder. A configuration belongs to one decoder type and one
  // protocol, so the options have to be worked out per row rather than shared.
  const { data: ioConfigs } = useSearchAllIOConfig(interfaceId)
  const { data: protocolData } = useDecoderProtocolMap(interfaceId)

  const getOptionsForRow = useCallback(
    (row: any) => {
      // A decoder's configs are those of its own sub-controller. Config ids
      // repeat across sub-controllers, so scope to the decoder's interface
      // before matching by type/protocol (and before reading the current
      // config's protocol).
      const subId = Number(row?.interfaceId) || interfaceId
      const subConfigs =
        ioConfigs?.filter((cfg) => Number(cfg.interfaceId) === subId) ?? []
      return getIOConfigOptions(
        subConfigs,
        Number(row?.typeId) || ioConfigType,
        resolveDecoderProtocol(
          subConfigs,
          protocolData?.protocolByAddress.get(Number(row?.address)),
          Number(row?.configurationId),
        ),
      )
    },
    [ioConfigs, protocolData, ioConfigType, interfaceId],
  )

  // Resolve a row's line-unit protocol the same way the config filter does —
  // line-unit map first, then the decoder's current config as a fallback — so
  // mk3 decoders that carry no line-unit entry (but an mk3 config) still count.
  //
  // The protocol itself rather than one flag: the panel shows readings for mk3
  // and withholds Autoswitch from mk2, and 0 (nothing resolved) has to be
  // distinguishable from both — an unknown protocol keeps every control it
  // would have had, since hiding one on a guess takes away a control that
  // works.
  const resolveRowProtocol = useCallback(
    (row: any) => {
      const subId = Number(row?.interfaceId) || interfaceId
      const subConfigs =
        ioConfigs?.filter((cfg) => Number(cfg.interfaceId) === subId) ?? []
      return (
        resolveDecoderProtocol(
          subConfigs,
          protocolData?.protocolByAddress.get(Number(row?.address)),
          Number(row?.configurationId),
        ) ?? 0
      )
    },
    [ioConfigs, protocolData, interfaceId],
  )

  const availableColumns = useMemo(
    () =>
      COLUMN_CONFIG.filter((col) => {
        if ((col as any).guidedOnly && !embedded) return false
        if (col.conditionalHide === 'tabOutput' && tab !== '0') return false
        if (col.conditionalHide === 'controlHub' && !hasControlHub) return false
        if (col.conditionalHide === 'bulkEdit' && (!bulkEditMode || embedded))
          return false
        // Dropped here rather than through the visibility state below: this
        // grid has no column picker and force-shows everything it makes
        // available, so a hidden column would come straight back.
        if (review && REVIEW_HIDDEN_COLUMNS.includes(col.field)) return false
        return true
      }),
    [tab, hasControlHub, embedded, review, bulkEditMode],
  )
  const gridColumns = useMemo(() => {
    if (!embedded) return availableColumns
    // Columns the order does not name keep their relative position, after the
    // ones it does.
    const rank = (field: string) => {
      const index = GUIDED_COLUMN_ORDER.indexOf(field)
      return index < 0 ? GUIDED_COLUMN_ORDER.length : index
    }
    return availableColumns
      .filter((column) => !['value', 'verifySum'].includes(column.field))
      .sort((a, b) => rank(a.field) - rank(b.field))
  }, [availableColumns, embedded])

  // The decoder list has no column picker: which columns exist is decided by
  // the tab, the interface and the guided embed, so every column this pass
  // makes available is shown. Pushed through the api rather than left to the
  // defs so a layout an earlier build's picker hid does not survive here.
  useEffect(() => {
    const api = gridApiRef.current
    if (!api) return
    availableColumns.forEach((col) => {
      api.setColumnsVisible([col.field], true)
    })
  }, [storageTab, availableColumns])

  // Switching tab or interface starts a different result set, so drop any
  // pending edits rather than carrying them across.
  useEffect(() => {
    editedRowsRef.current = {}
    savedPurposeRef.current = {}
    setEditVersion((version) => version + 1)
    gridApiRef.current?.clearPendingEdits()
  }, [interfaceId, tab])

  useEffect(() => {
    if (isSuccess && toastMessage) {
      toast.success(toastMessage)
      setToastMessage('')
    }
  }, [isSuccess, toastMessage])

  const setRowLoading = useCallback((rowKey: string, loading: boolean) => {
    setLoadingRows((prev) => ({ ...prev, [rowKey]: loading }))
    setGlobalLoading(loading)
  }, [])

  useEffect(() => {
    if (isErrorAdd) {
      setLoadingRows({})
      setGlobalLoading(false)
    }
  }, [isErrorAdd])

  // On / Off / Ping / Autoswitch are the same call with a different type.
  const runDecoderCommand = useCallback(
    (type: number, successMessage: string) =>
      (id: string, address: string, rowKey: string) => {
        if (isViewOnly) return

        setRowLoading(rowKey, true)

        addController(
          {
            id: id,
            interfaceId,
            address: Number(address),
            type,
          },
          {
            onError: () => {
              // No toast here: `useAddController` raises one for every caller,
              // and this second handler made one refusal arrive as two popups —
              // its own generic "Something Went Wrong" beside the hook's copy of
              // the server's actual reason (SCRUM-1032 QC, 21 Sep). React Query
              // runs both the mutation's `onError` and this one.
              setRowLoading(rowKey, false)
            },
            onSuccess: () => {
              setRowLoading(rowKey, false)
              setToastMessage(successMessage)
            },
          },
        )
      },
    [addController, interfaceId, isViewOnly, setRowLoading],
  )

  const handleOn = useMemo(
    () => runDecoderCommand(1, t('OnSuccess')),
    [runDecoderCommand, t],
  )
  const handleOff = useMemo(
    () => runDecoderCommand(2, t('OffSuccess')),
    [runDecoderCommand, t],
  )
  const handleAutoswitch = useMemo(
    () => runDecoderCommand(3, t('AutoswitchSuccess')),
    [runDecoderCommand, t],
  )
  const handlePing = useMemo(
    () => runDecoderCommand(4, t('PingSuccess')),
    [runDecoderCommand, t],
  )

  // Reviewed state is per interface + per decoder type, and is reloaded rather
  // than carried when either changes.
  useEffect(() => {
    if (!embedded) return
    const stored = loadGuidedDecoderProgress(interfaceId, progressTab)
    setEditedRowKeys(
      new Set(
        (stored?.editedRowKeys ?? []).filter((key) => !isLegacyAddressKey(key)),
      ),
    )
    // A different list: nothing in it has been written by this sitting yet.
    setSavedRowKeys(new Set())
    savedFieldsRef.current = {}
  }, [embedded, interfaceId, progressTab])

  const markEdited = useCallback(
    (rowKey: string) => {
      if (!embedded) return
      setEditedRowKeys((previous) => {
        if (previous.has(rowKey)) return previous
        const next = new Set(previous).add(rowKey)
        saveGuidedDecoderProgress(interfaceId, progressTab, {
          editedRowKeys: [...next],
        })
        return next
      })
    },
    [embedded, interfaceId, progressTab],
  )

  // The guided list knows its own size; only the paged one has to be told.
  const totalRowCount = embedded ? (allDecoders?.length ?? null) : serverTotal

  // Which rows the hide-edited filter is hiding, decided when it was switched
  // on rather than re-read as the user works.
  //
  // A live filter took the row away the moment a cell in it was edited: the
  // editor closed, the rows below jumped up, and whatever else that row needed
  // was gone with it. Editing a row is not a request to stop looking at it.
  // The set is fixed at switch-on, and rows edited since stay put; they drop
  // out the next time the filter is switched on, which is the user's own
  // action rather than another control to notice.
  const [hiddenRowKeys, setHiddenRowKeys] = useState<Set<string> | null>(null)
  const editedRowKeysRef = useRef(editedRowKeys)
  editedRowKeysRef.current = editedRowKeys

  /**
   * The rows the missing-purpose filter shows, fixed when it is switched on for
   * the same reason: a row must not vanish the moment its purpose is picked.
   */
  const [shownRowKeys, setShownRowKeys] = useState<Set<string> | null>(null)
  const missingPurposeKeysRef = useRef(missingPurposeKeys)
  missingPurposeKeysRef.current = missingPurposeKeys

  useEffect(() => {
    if (!embedded || editedFilter !== 'missingPurpose') {
      setShownRowKeys(null)
      return
    }
    setShownRowKeys(new Set(missingPurposeKeysRef.current ?? []))
    // Only on switch-on, as below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, editedFilter])

  useEffect(() => {
    if (!embedded || editedFilter !== 'notEdited') {
      setHiddenRowKeys(null)
      return
    }
    setHiddenRowKeys(new Set(editedRowKeysRef.current))
    // Only when the filter itself is turned on: taking `editedRowKeys` as a
    // dependency is exactly the live behaviour this exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, editedFilter])

  const rowFilter = useMemo(() => {
    if (!embedded || editedFilter === 'all') return undefined
    if (editedFilter === 'missingPurpose') {
      const shown = shownRowKeys ?? missingPurposeKeys ?? new Set<string>()
      return (row: DecoderRow) => shown.has(rowKeyOf(row))
    }
    if (editedFilter === 'edited') {
      // Showing only edited rows adds them as they are edited, which is not
      // disruptive: nothing the user is working on is taken away.
      return (row: DecoderRow) => editedRowKeys.has(rowKeyOf(row))
    }
    const hidden = hiddenRowKeys ?? editedRowKeys
    return (row: DecoderRow) => !hidden.has(rowKeyOf(row))
  }, [
    embedded,
    editedFilter,
    editedRowKeys,
    hiddenRowKeys,
    shownRowKeys,
    missingPurposeKeys,
  ])

  useEffect(() => {
    if (!embedded) return
    onEditedProgress?.({
      edited: editedRowKeys.size,
      total: totalRowCount,
      missingPurpose: missingPurposeCount,
    })
  }, [
    embedded,
    onEditedProgress,
    editedRowKeys,
    totalRowCount,
    missingPurposeCount,
  ])

  // The marker is the row, not a column: a whole column of badges costs width
  // the decoder values need, and the tint reads at a glance down a long list.
  const guidedRowClass = useMemo(() => {
    if (!embedded) return undefined
    return ({ rowId }: { rowId: string }) => {
      // Written beats touched: once the row is on the server, "you have been
      // here" is the less useful of the two things the tint can say.
      if (savedRowKeys.has(rowId)) return gridRowClasses.saved
      return editedRowKeys.has(rowId) ? gridRowClasses.touched : undefined
    }
  }, [embedded, editedRowKeys, savedRowKeys])

  // Hides rows without touching the query — the server has no field for
  // "this user edited it", so the filter can only run over what has loaded.
  const getRowId = useCallback((row: DecoderRow) => rowKeyOf(row), [])

  const columns = useMemo<GridColumn<DecoderRow>[]>(
    () =>
      gridColumns.map((col) => {
        const valueType = (col as any).valueType as string | undefined
        const isEditable = Boolean((col as any).editable) && bulkEditMode

        const column: GridColumn<DecoderRow> = {
          field: col.field,
          headerName:
            embedded && GUIDED_HEADER_OVERRIDE_KEYS[col.field]
              ? t(GUIDED_HEADER_OVERRIDE_KEYS[col.field])
              : col.headerName
                ? t(col.headerName)
                : '',
          minWidth: col.minWidth,
          maxWidth: (col as any).maxWidth,
          width: (col as any).width,
          // ag's defaultColDef gave every column `flex: 1`; the fixed-width
          // control column is the one exception.
          flex: (col as any).width ? undefined : 1,
          pinned: (col as any).pinned,
          sortable: col.sortable === true,
          resizable: (col as any).resizable !== false,
          editable: isEditable,
          required: requiresPurpose && col.field === 'decoderType',
          cellClass: ({ row }) => {
            const list: string[] = []
            if (bulkEditMode) {
              list.push(
                isEditable
                  ? gridCellClasses.editable
                  : gridCellClasses.nonEditable,
              )
            }
            const rowKey = rowKeyOf(row)
            const pending = editedRowsRef.current[rowKey]
            if (pending?.__changedFields?.has?.(col.field)) {
              list.push(gridCellClasses.changed)
            } else if (savedFieldsRef.current[rowKey]?.has(col.field)) {
              // Written this pass, and still showing the value that was sent.
              list.push(gridCellClasses.saved)
            }
            // Only the cell that needs action, not the row (Wei, round 1).
            if (
              requiresPurpose &&
              col.field === 'decoderType' &&
              !hasPurpose(pending ?? row)
            ) {
              list.push(gridCellClasses.invalid)
            }
            return list
          },
        }

        if (valueType === 'addressIndex') {
          column.valueGetter = (row) =>
            row ? `${row.address}-${row.index}` : ''
          // The value is merged for reading; the server still sorts on address.
          column.sortField = 'address'
        }

        if (valueType === 'valueModified')
          column.cellRenderer = ValueModifiedCellRenderer
        if (valueType === 'controller')
          column.cellRenderer = ControllerChipCellRenderer
        if (valueType === 'controlPanel')
          column.cellRenderer = ControlPanelCellRenderer
        if (valueType === 'deleteAction')
          column.cellRenderer = DeleteActionCellRenderer

        if (col.field === 'decoderType') {
          column.filter = 'set'
          // Two different filters wearing the same face. /config pages from the
          // server, which filters Purpose by decoder type id, so the model
          // carries ids and no row is ever matched locally. The guided list
          // holds every decoder in memory and filters itself, where the only
          // thing a row can be matched on is what it actually holds — the
          // purpose *name*. Offering ids there matched nothing at all.
          column.filterParams = embedded
            ? { values: decoderTypeOptions }
            : {
                values: (decoderTypeData?.list ?? []).map((dt) =>
                  Number(dt.id),
                ),
                formatValue: (value) =>
                  decoderTypeData?.list?.find(
                    (dt) => Number(dt.id) === Number(value),
                  )?.name ?? String(value),
              }
        }

        if (valueType === 'richSelect') {
          column.cellRenderer = bulkEditMode
            ? PurposeDropdownCellRenderer
            : PurposeChipCellRenderer
          if (bulkEditMode) {
            column.editor = 'select'
            column.editorParams = {
              values: groupedPurposeOptions,
              groupBy: (option: string) =>
                t(PURPOSE_GROUP_LABEL_KEY[purposeGroupOf(option)]),
            }
          }
        }

        if (valueType === 'richSelectConfig') {
          column.cellRenderer = bulkEditMode
            ? ConfigDropdownCellRenderer
            : ConfigCellRenderer
          if (bulkEditMode) {
            column.editor = 'select'
            // A function so every row is offered the configurations that match
            // its own decoder type and protocol, instead of one shared list.
            column.editorParams = ({ row }) => ({
              values: getOptionsForRow(row).map((option) => option.label),
            })
          }
        }

        // Decoder Name is the one editable column with no dropdown behind it.
        if (isEditable && !column.editor) column.editor = 'text'

        return column
      }),
    // `cellClass` reads `editedRowsRef`, which is mutated in place, so the rule
    // cannot see why `editVersion` belongs here: the counter is what tells this
    // memo to rebuild and repaint the pending-change highlight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      gridColumns,
      bulkEditMode,
      embedded,
      decoderTypeOptions,
      groupedPurposeOptions,
      decoderTypeData,
      getOptionsForRow,
      t,
      editVersion,
      requiresPurpose,
    ],
  )

  // --- Purpose changes that would strand another record (SCRUM-1032) --------

  // Only in the wizard, and only the lists a decoder can be claimed by. On
  // /config these are separate screens with their own guards. Not in the
  // discovery review either: it is read-only, so no purpose changes there.
  const checksPurposeImpact = embedded && !review && Number(interfaceId) > 0
  const { data: stationList } = useSearchStation(
    { PageSize: 0 },
    Number(interfaceId),
    { enabled: checksPurposeImpact } as any,
  )
  // Its own read, every valve on this interface: the shared valve list pages
  // at the backend's default 10 and is not keyed by interface.
  const { data: valveRows } = useFlowValvesForPurposeCheck(Number(interfaceId), {
    enabled: checksPurposeImpact,
  })

  /**
   * SCRUM-1145 — decoders whose stations no longer agree with them, by
   * `address-index`. Two ways that happens on /config:
   *
   * - `stranded`: the purpose moved off Irrigation / Non-Irrigation (the wizard
   *   asks first, SCRUM-1032; here it is allowed), so the station points at a
   *   pump or a valve. Put the purpose back, or remove the station.
   * - `mismatch`: still a station purpose, but the station is the other Type —
   *   the decoder save retypes it, and the server refuses an Irrigation station
   *   with no pipe. Give the station a pipe and its Type, or put the purpose
   *   back.
   *
   * Either way the decoder's purpose stays red, with an info button naming the
   * station and the fix, after the save as well — which is why this is worked
   * out from the stations rather than remembered: each station carries its
   * decoder's purpose and its own Type.
   */
  const decoderConflicts = useMemo(() => {
    const conflicts = new Map<string, DecoderConflict>()
    if (embedded || tab !== '0') return conflicts
    const stations = decoderStations ?? []

    stations.forEach((station: any) => {
      const key = stationDecoderKeyOf(station)
      const staged = editedRowsRef.current[key]
      const purposeStaged = Boolean(staged?.__changedFields?.has('decoderType'))
      const purpose = purposeStaged
        ? String(staged.decoderType ?? '').trim()
        : stationPurposeOf(station)
      const kind = conflictKindOf(station, purpose, purposeStaged)
      if (!kind) return

      const entry: DecoderConflict = conflicts.get(key) ?? {
        kind,
        purpose,
        stations: [],
        irrigationNeeds: [],
      }
      entry.stations.push(String(station.description || station.id))
      if (kind === 'mismatch' && stationTypeForPurpose(purpose) === 2) {
        irrigationFieldsMissing(station).forEach((field) => {
          if (!entry.irrigationNeeds.includes(field))
            entry.irrigationNeeds.push(field)
        })
      }
      conflicts.set(key, entry)
    })
    return conflicts
    // `editVersion` is what moves when `editedRowsRef` is written in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, tab, decoderStations, editVersion])

  const rowClass = embedded ? guidedRowClass : undefined

  const gridContext = useMemo<ControllersGridContextValue>(
    () => ({
      tab,
      interfaceId,
      isDark,
      resolveRowProtocol,
      onOn: handleOn,
      onOff: handleOff,
      onPing: handlePing,
      onAutoswitch: handleAutoswitch,
      onDelete: setDeleteTarget,
      onDecoderDeleted: handleDecoderDeleted,
      loadingRows,
      globalLoading,
      decoderConflicts,
    }),
    [
      tab,
      interfaceId,
      isDark,
      resolveRowProtocol,
      handleOn,
      handleOff,
      handlePing,
      handleAutoswitch,
      handleDecoderDeleted,
      loadingRows,
      globalLoading,
      decoderConflicts,
    ],
  )

  // Every purpose change waiting on the dialog. A fill or paste across several
  // rows asks about each row in the same tick; they are put to the user
  // together rather than each replacing the last.
  const [purposePrompts, setPurposePrompts] = useState<
    PurposePromptEntry<DecoderRow>[]
  >([])
  const [isDeletingDependents, setIsDeletingDependents] = useState(false)

  /** Stages the edit the prompt was raised about, once the user has agreed. */
  const stageEditRef = useRef<
    ((row: DecoderRow, field: string, value: unknown) => void) | null
  >(null)

  const dependentsOf = useCallback(
    (row: DecoderRow, nextPurposeName: string): PurposeChangeImpact => {
      if (!embedded) return NO_IMPACT
      return purposeChangeImpact({
        decoder: {
          id: (row as any)?.id,
          address: (row as any)?.address,
          index: (row as any)?.index,
          twiId: (row as any)?.twiId ?? null,
        },
        nextPurposeName,
        stations: (stationList?.list ?? []) as any[],
        valves: (valveRows ?? []) as any[],
      })
    },
    [embedded, stationList, valveRows],
  )

  const handleKeepPurpose = useCallback(() => {
    // Nothing was staged, so the cells still hold the purposes they had — the
    // dialog closing is the whole of the revert.
    setPurposePrompts([])
  }, [])

  const handleProceedWithDelete = useCallback(async () => {
    if (purposePrompts.length === 0) return
    setIsDeletingDependents(true)
    // Valves first, then stations: same order as everywhere else here, and
    // one at a time — these endpoints answer 500 under concurrent writes.
    const { removed, error } = await removeDependents(
      combinedImpact(purposePrompts),
      {
        valve: (id) => flowValveApi.delete(String(interfaceId), String(id)),
        station: (id) =>
          stationDataConfigApi.delete(String(interfaceId), String(id)),
      },
    )
    // Whatever went, the station and valve lists are now stale — on a partial
    // failure too. Left alone, the next prompt lists records already deleted
    // and Proceed answers 404.
    if (removed.stations.length > 0 || error) {
      queryClient.invalidateQueries([queryKeys.stations])
    }
    if (removed.valves.length > 0 || error) {
      queryClient.invalidateQueries([queryKeys.flowValve])
    }

    if (error) {
      // The purposes are left as they were: a half-done cascade must not be
      // sold to the user as a completed one. What did go is taken off the
      // dialog, so trying again does not delete it twice.
      toast.error(getApiErrorMessage(error))
      setPurposePrompts((held) => withoutRemoved(held, removed))
    } else {
      purposePrompts.forEach((entry) =>
        stageEditRef.current?.(entry.row, 'decoderType', entry.toPurpose),
      )
      setPurposePrompts([])
    }
    setIsDeletingDependents(false)
  }, [purposePrompts, interfaceId, queryClient])

  const handleCellValueChanged = useCallback(
    (event: { row: DecoderRow; field: string; newValue: unknown }) => {
      if (!bulkEditMode) return false as const
      // No id, no decoder: a stand-in row for a block still loading. Staged,
      // it saved as PUT /api/Controller/{interface}/undefined and failed.
      if (!event.row?.id) return false as const

      const rowId = rowKeyOf(event.row)

      // SCRUM-1032: a purpose is not only a label. A station holds an
      // irrigation decoder and a valve holds a master-valve one; changing it
      // out from under them leaves a record pointing at a decoder that no
      // longer does its job. Ask first — the edit is not staged until the user
      // has said which way to resolve it.
      if (event.field === 'decoderType') {
        const impact = dependentsOf(event.row, String(event.newValue ?? ''))
        if (hasImpact(impact)) {
          setPurposePrompts((held) =>
            addPurposePrompt(held, {
              rowId,
              row: event.row,
              decoderAddress: `${(event.row as any)?.address}-${(event.row as any)?.index}`,
              fromPurpose: String((event.row as any)?.decoderType ?? ''),
              toPurpose: String(event.newValue ?? ''),
              impact,
            }),
          )
          // Rejected, so the cell keeps the purpose it had. Proceeding stages
          // it again through `stageEditRef`.
          return false as const
        }
      }

      // Track which fields changed on the pending edit itself rather than on
      // the row the grid holds: the block cache can drop and refetch that row,
      // taking any state written onto it along.
      const previous = editedRowsRef.current[rowId]
      const changedFields = new Set<string>(previous?.__changedFields ?? [])
      changedFields.add(event.field)

      editedRowsRef.current[rowId] = {
        ...previous,
        ...event.row,
        [event.field]: event.newValue,
        __changedFields: changedFields,
      }
      setEditVersion((version) => version + 1)
      noteUserEdit()
      // A fresh edit is the user back at work: the Stop they pressed applied
      // to the batch they stopped, not to everything after it.
      stoppedRef.current = false
      onAutosaveStateChange?.('pending')
      markEdited(rowId)

      // No `false` here. Rejecting the edit left the cell showing its old value
      // until the next block arrived — amber, as if changed, with the previous
      // text still in it, and the typed value only appearing once autosave had
      // written and refetched. Letting the grid stage it (`batchEdit`) renders
      // the row as base ⊕ staged edit straight away; `editedRowsRef` stays the
      // source for the save, and `transformBlock` still re-applies it to blocks
      // the cache refetches underneath.
    },
    [bulkEditMode, onAutosaveStateChange, markEdited, dependentsOf, noteUserEdit],
  )

  // The same staging the handler does, minus the guard — used once the user has
  // agreed to the deletions, where asking again would be absurd.
  stageEditRef.current = (row, field, value) => {
    const rowId = rowKeyOf(row)
    const previous = editedRowsRef.current[rowId]
    const changedFields = new Set<string>(previous?.__changedFields ?? [])
    changedFields.add(field)
    editedRowsRef.current[rowId] = {
      ...previous,
      ...row,
      [field]: value,
      __changedFields: changedFields,
    }
    /**
     * SCRUM-1032 QC round 4, item 3 — into the grid's buffer too.
     *
     * A cell edited the ordinary way is staged in both places: `editedRowsRef`
     * is what the save reads, and the grid's own pending-edit buffer is what
     * keeps the value on screen (`batchEdit` renders base ⊕ staged edit). This
     * path — a purpose the user confirmed through the dependents dialog — only
     * ever wrote the first of the two. So the value showed while the ref held
     * it, and vanished the moment the save retired that entry, leaving an empty
     * required cell behind: the "previous assign disappear" Wei Woon caught
     * mid-save on 27 Sep, on exactly the rows a purpose change asks about.
     */
    gridApiRef.current?.setPendingEdit(rowId, { [field]: value } as any)
    setEditVersion((version) => version + 1)
    noteUserEdit()
    stoppedRef.current = false
    onAutosaveStateChange?.('pending')
    markEdited(rowId)
  }

  const runSave = useCallback(async (): Promise<GuidedSaveResult> => {
    /**
     * SCRUM-1032 QC, 5 Oct — the list is read-only while it writes.
     *
     * "When editted data is saving (Decoder / station), possible to make the
     * table uneditable? because some of the data that is edit during the saving
     * is not shown." An editor open when the pass starts holds a value that was
     * never staged, so it is committed first; `editMode` then keeps new editors
     * from opening until the pass ends.
     */
    commitOpenEditor(gridApiRef.current)

    // An edit with no decoder id has nothing to write to — it can only be a
    // stand-in row for a block that had not loaded. Dropped rather than sent,
    // so it cannot fail the batch as "1 of N rows could not be saved".
    Object.entries(editedRowsRef.current).forEach(([key, row]) => {
      if (!(row as any)?.id) delete editedRowsRef.current[key]
    })
    const editedRows = Object.values(editedRowsRef.current)
    if (editedRows.length === 0) {
      onAutosaveStateChange?.('saved', { lastSavedAt: Date.now() })
      if (!embedded) onToggleBulkEdit?.()
      return { failed: 0, total: 0 }
    }

    stopRequestedRef.current = false

    try {
      const total = editedRows.length
      /**
       * Rows settled — written or given up on. What the toolbar counts, so the
       * readout reaches its total whatever happened to the rows.
       */
      let done = 0
      const reportProgress = () =>
        onAutosaveStateChange?.('saving', {
          progress: { done, total },
          onStop: () => {
            stopRequestedRef.current = true
            stoppedRef.current = true
          },
        })

      reportProgress()

      /**
       * SCRUM-1145 — a decoder moved between Irrigation and Non-Irrigation
       * takes its station's Type with it. Only on /config: in the wizard the
       * Station step does this from the decoder list (SCRUM-1157).
       */
      const stationRetype = embedded ? null : createStationRetype(interfaceId)
      /** Stations left on their old Type because their write failed. */
      const retypeFailedNames: string[] = []
      /** The station list carries each decoder's purpose; it is stale after this. */
      let purposeSaved = false

      // The dropdown holds labels, not ids, so the id is resolved back within
      // the options that row was actually offered. On a miss it keeps the id
      // the row already had — returning 0 reset the decoder's configuration to
      // "Unknown" on every bulk save.
      const getConfigurationId = (row: any): number =>
        resolveIOConfigId(
          getOptionsForRow(row),
          row.configuration,
          row.configurationId,
        )

      /**
       * Rows whose Purpose reads as a name this screen cannot turn into an id.
       * Collected rather than written: `decoderType: null` clears the column,
       * so the old fallback destroyed the purpose it could not resolve
       * (SCRUM-1032 QC, 27 Sep).
       */
      const unresolvedPurposes: { name: string; purpose: string }[] = []

      // Decoders are updated one at a time: the API returns 500 when writes
      // arrive concurrently, so a Promise.all over the edited rows loses most
      // of them on a large selection.
      let failed = 0
      let lastError: any = null
      /** Which decoders failed, so the toast can say rather than just count. */
      const failedNames: string[] = []
      /** Rows the user abandoned by pressing Stop: staged, not attempted. */
      let skipped = 0

      for (const row of editedRows) {
        if (stopRequestedRef.current) {
          skipped = total - done
          break
        }

        const rowId = rowKeyOf(row)
        let saved = false

        const purpose = resolveDecoderPurposeId(row, decoderTypeData?.list)
        if ('unresolved' in purpose) {
          // Not attempted at all. The row keeps its staged value and its amber,
          // so nothing the user entered is lost and they can see which rows
          // were left behind.
          failed++
          failedNames.push(String(row.name || rowId))
          unresolvedPurposes.push({
            name: String(row.name || rowId),
            purpose: purpose.unresolved,
          })
          done++
          setEditVersion((version) => version + 1)
          reportProgress()
          continue
        }

        for (let attempt = 0; attempt < SAVE_RETRY_LIMIT && !saved; attempt++) {
          try {
            await controllerDataConfigApi.update({
              // A decoder on a control hub belongs to a sub-controller, so it
              // must be written back to its own interface, not the hub's. The
              // row carries that original interface id; fall back to the current
              // interface for a plain (non-hub) controller.
              interfaceId: Number(row.interfaceId) || interfaceId,
              address: row.address,
              id: row.id,
              name: row.name,
              decoderType: purpose.id,
              configuration: getConfigurationId(row),
              // Carry the decoder's existing switch parameters through. This
              // request replaces the row, so sending literal zeros here wiped
              // the inrush/holding voltage configured on the device.
              verify: row.verify ?? false,
              force: row.force ?? false,
              inrushVoltage: row.inrushVoltage ?? 0,
              inrushTime: row.inrushTime ?? 0,
              holdingVoltage: row.holdingVoltage ?? 0,
              overrideSwitchParams: row.overrideSwitchParams ?? false,
              overrideOptions: row.overrideOptions ?? false,
              twiId: row.twiId,
            })
            saved = true
          } catch (error: any) {
            lastError = error
            // A 4xx is the server refusing this row; sending it again gets
            // the same answer (seen on D2: one 404 sent three times).
            if (!isRetryableApiError(error)) break
            if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
          }
        }

        if (saved && row.__changedFields?.has('decoderType')) {
          purposeSaved = true
          if (stationRetype) {
            const failedStations = await stationRetype.apply(
              row as any,
              String((row as any).decoderType ?? ''),
            )
            retypeFailedNames.push(...failedStations)
          }
        }

        if (saved) {
          // Retired here, not at the end of the pass. Identity-guarded: an edit
          // made to this same row while its request was in flight is a new
          // object, and must survive to be written by the next pass.
          // Read only on the output tab, and dropped on a tab or interface
          // change, so recording it for every row costs nothing.
          savedPurposeRef.current[rowId] = String((row as any).decoderType ?? '')
          if (editedRowsRef.current[rowId] === row) {
            delete editedRowsRef.current[rowId]
            // What the amber was marking, so those cells can go green rather
            // than plain while their staged values are still on screen.
            savedFieldsRef.current[rowId] = new Set(row.__changedFields ?? [])
          }
          setSavedRowKeys((previous) => {
            if (previous.has(rowId)) return previous
            return new Set(previous).add(rowId)
          })
        } else {
          // Left staged and amber, so the user can retry just those.
          failed++
          failedNames.push(String(row.name || rowId))
        }

        // The `cellClass` memo reads `editedRowsRef`, which is mutated in
        // place; this counter is what tells it to repaint. One bump per row is
        // what drops the amber a row at a time.
        done++
        setEditVersion((version) => version + 1)
        reportProgress()

        await sleep(SAVE_REQUEST_GAP_MS)
      }

      /**
       * The list first, then the staged copies — not the other way round.
       *
       * SCRUM-1032 QC round 6, item 2. Clearing the grid's buffer and firing an
       * unawaited refetch left a window where the rows on screen were the ones
       * from before the save and nothing was staged over them, so every written
       * cell showed its old value until the response arrived. Awaiting the read
       * first means the new values are already in the rows when the staged ones
       * are dropped, and nothing flickers back.
       *
       * Rows that failed stay in `editedRowsRef`, which `guidedRows` and
       * `transformBlock` re-apply, so they keep their value and their amber.
       */
      await refreshRows()
      gridApiRef.current?.clearPendingEdits()

      if (purposeSaved) queryClient.invalidateQueries([queryKeys.stations])
      if (retypeFailedNames.length > 0) {
        const shownNames = retypeFailedNames.slice(0, MAX_FAILED_NAMES_SHOWN)
        toast.error(
          t('controllersStationRetypeFailed', {
            names:
              retypeFailedNames.length > shownNames.length
                ? `${shownNames.join(', ')}, …`
                : shownNames.join(', '),
          }),
        )
      }
      // The staged values go with it, so the per-cell green has nothing left to
      // mark — the row tint is what says "written" from here on.
      savedFieldsRef.current = {}

      // Rows the pass actually reached. A stopped batch is judged on those
      // alone: the ones it never got to are neither saved nor failed.
      const attempted = total - skipped

      /**
       * Rows refused before any request: there is no server error to quote, so
       * the toast has to say why itself, naming the purpose it could not place.
       */
      const distinctUnresolved = Array.from(
        new Set(unresolvedPurposes.map((entry) => entry.purpose)),
      )
      const unresolvedReason = distinctUnresolved.length
        ? t('decoderPurposeUnknownType', {
            purposes: distinctUnresolved
              .slice(0, MAX_FAILED_NAMES_SHOWN)
              .join(', '),
          })
        : ''

      if (attempted === 0) {
        // Stopped before anything was written. The staged rows are still there
        // and still amber, which is the whole report.
        onAutosaveStateChange?.('pending')
      } else if (failed === 0) {
        // In the wizard the step reports its own saves; a success toast per
        // flush (e.g. on Exit) read as noise over the next screen.
        if (!embedded) toast.success(t('UpdateDecoderSuccess'))
        // Stopped part-way through a clean run: what landed, landed, but there
        // is work left, so the pill must not claim the list is saved.
        onAutosaveStateChange?.(skipped > 0 ? 'pending' : 'saved', {
          lastSavedAt: Date.now(),
        })
        if (!embedded && skipped === 0) onToggleBulkEdit?.()
      } else if (failed === attempted) {
        onAutosaveStateChange?.('error')
        toast.error(
          lastError
            ? getApiErrorMessage(lastError, t('stationsToastSaveFailed'))
            : unresolvedReason || t('stationsToastSaveFailed'),
        )
      } else {
        onAutosaveStateChange?.('error')
        // Named, with the server's reason: a bare count left QC with nothing
        // to go on — not which decoder, not why.
        const shownNames = failedNames.slice(0, MAX_FAILED_NAMES_SHOWN)
        const names =
          failedNames.length > shownNames.length
            ? `${shownNames.join(', ')}, …`
            : shownNames.join(', ')
        const reason = lastError
          ? getApiErrorMessage(lastError, unresolvedReason)
          : unresolvedReason
        toast.error(
          t('bulkSavePartialErrorDetail', {
            failed,
            total: attempted,
            names,
            reason,
          }),
        )
      }
      /**
       * SCRUM-1032 QC round 6, item 4 — what holds the wizard on this step.
       *
       * Rows the user stopped the batch before reaching count with the ones
       * that failed: both leave a staged row behind, and moving on is what
       * loses it. `runGuidedSaves` reads this.
       */
      return { failed: failed + skipped, total }
    } catch (error: any) {
      onAutosaveStateChange?.('error')
      toast.error(
        getApiErrorMessage(error, t('stationsToastSaveFailed')),
      )
      // The pass blew up rather than reporting per row, so nothing is confirmed.
      return { failed: editedRows.length, total: editedRows.length }
    }
  }, [
    onToggleBulkEdit,
    onAutosaveStateChange,
    embedded,
    getOptionsForRow,
    decoderTypeData,
    interfaceId,
    queryClient,
    refreshRows,
    t,
  ])

  /**
   * One pass at a time, and a forced save waits for the running one rather
   * than skipping past it.
   *
   * The debounce is restarted by every row a pass retires, so on a batch
   * slower than the delay the timer fires mid-flush; that call is dropped, and
   * the pass's own last bump reschedules it. A forced call is the user asking
   * — Save, or the wizard moving on — and must not return while rows are still
   * unwritten, so it awaits the pass in flight and then runs its own over
   * whatever is left (rows that failed, and edits made during the batch).
   */
  const handleSave = useCallback(
    async (options?: { force?: boolean }): Promise<GuidedSaveResult> => {
      // Nothing of this call's own ran, so it reports nothing failed: only
      // the background autosave takes this path, and it ignores the result.
      if (inFlightRef.current && !options?.force) return { failed: 0, total: 0 }
      // Stopped by the user. Only the background autosave is silenced; Save and
      // the wizard's flush come through `force`, and any new edit clears it.
      if (stoppedRef.current && !options?.force) return { failed: 0, total: 0 }

      // Waits until no pass runs at all, then starts in the same tick — two
      // forced saves waiting on one pass used to both start their own and
      // write the same rows twice (round 7 review).
      return runWhenIdle(inFlightRef, () => {
        setIsSaving(true)
        const pass = runSave().finally(() => {
          if (inFlightRef.current === pass) {
            inFlightRef.current = null
            setIsSaving(false)
          }
        })
        inFlightRef.current = pass
        return pass
      })
    },
    [runSave],
  )

  useGuidedAutosave({
    enabled: embedded && bulkEditMode,
    hasPendingChanges: Object.keys(editedRowsRef.current).length > 0,
    version: userEditVersion,
    save: handleSave,
    // An open editor is the user mid-value; the save would close it.
    isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
    // Read through a ref so the held timer sees the pause lift.
    isBusy: () => autosavePausedRef.current,
    // This list reports its own progress, with a row count and a Stop.
    reportsOwnState: true,
  })

  const handleCancel = useCallback(() => {
    editedRowsRef.current = {}
    savedFieldsRef.current = {}
    savedPurposeRef.current = {}
    stoppedRef.current = false
    setEditVersion((version) => version + 1)
    gridApiRef.current?.clearPendingEdits()
    refreshRows()
  }, [refreshRows])

  useImperativeHandle(
    ref,
    () => ({
      // Asked for by hand — through Save, or by the wizard moving on — so it
      // runs even if the background autosave was stopped.
      handleSave: (options) => handleSave({ force: true, ...options }),
      handleCancel,
      hasPendingChanges: () => Object.keys(editedRowsRef.current).length > 0,
      refresh: refreshRows,
    }),
    [handleSave, handleCancel, refreshRows],
  )

  // No loading gate here on purpose: unmounting the grid while a block loads
  // (which happened on every tab switch) throws away column widths and scroll
  // position. The grid draws skeleton rows instead.
  return (
    <Stack
      sx={{ flex: 1, height: '100%', minHeight: 0, width: '100%' }}
      className={classes.gridContainer}
    >
      <ControllersGridContext.Provider value={gridContext}>
        <DataGrid<DecoderRow>
          // Column state, visibility and the result set are all per tab, so a
          // tab change is a different grid rather than a reconfigured one.
          key={storageTab}
          columns={columns}
          getRowId={getRowId}
          // One or the other: the guided list is held whole, /config pages.
          {...(embedded
            ? { rows: guidedRows, loading: isFetchingAllDecoders || isImportSettling }
            : {
                serverDatasource: datasource,
                serverBlockSize: BLOCK_SIZE,
                serverTransformBlock: transformBlock,
                onServerCountChanged: setServerTotal,
              })}
          rowHeight={embedded ? EMBEDDED_ROW_HEIGHT : ROW_HEIGHT}
          rowFilter={rowFilter}
          rowClass={rowClass}
          // Read-only while the pass writes (QC, 5 Oct). `editMode` stops new
          // editors; the open one was committed at the start of the save.
          editMode={bulkEditMode && !isSaving ? 'singleClick' : 'none'}
          batchEdit
          onCellValueChanged={handleCellValueChanged}
          columnStateStorageKey={columnStateKey(storageTab)}
          columnStateUnprefixed
          apiRef={gridApiRef}
        />
      </ControllersGridContext.Provider>

      {deleteTarget && (
        <DeleteDecoderDialog
          open
          onClose={() => setDeleteTarget(null)}
          onDone={() => setDeleteTarget(null)}
          onDeleted={() => handleDecoderDeleted(deleteTarget)}
          interfaceId={Number((deleteTarget as any).interfaceId) || Number(interfaceId)}
          decoder={deleteTarget as any}
          protocol={resolveRowProtocol(deleteTarget)}
          isDark={isDark}
        />
      )}

      {purposePrompts.length > 0 && (
        <PurposeChangeDialog
          open
          decoderAddress={purposePrompts[0].decoderAddress}
          decoderCount={purposePrompts.length}
          fromPurpose={purposePrompts[0].fromPurpose}
          toPurpose={[...new Set(purposePrompts.map((entry) => entry.toPurpose))].join(' / ')}
          impact={combinedImpact(purposePrompts)}
          isDeleting={isDeletingDependents}
          onKeep={handleKeepPurpose}
          onProceed={handleProceedWithDelete}
        />
      )}
    </Stack>
  )
})

ControllersTableView.displayName = 'ControllersTableView'

export default ControllersTableView
